import hotkeys from 'hotkeys-js'
import {computed, Ref, ref} from 'vue'

interface SuggestedKeyEntry {
    key: string;
    nextKeys: string[]
}

const sequences = new Map<string[], Function>()
const nextSuggestedHotkeysSequence = ref<SuggestedKeyEntry[]>()

const pushedKeys: Ref<string[]> = ref([])
const lastThreePushedKeys = computed(() => pushedKeys.value.slice(-3))

const showHotkeysVisualizer = computed(() => {
    const keysToCheck = ['g', 'Shift', 'Control', 'Alt']
    return keysToCheck.some(key => pushedKeys.value.includes(key))
})

const resetKeys = () => {
    pushedKeys.value = []
    nextSuggestedHotkeysSequence.value = undefined
}

const getNextHotkeysSuggestions = (keys: string[]) => {
    nextSuggestedHotkeysSequence.value = Array.from(sequences.keys())
        .filter(keyArray => {
            return keys.every((key, i) => keyArray[i] === key)
        })
        .reduce((acc: SuggestedKeyEntry[], keyArray) => {
            const nextKey = keyArray.slice(1)
            const existingEntry = acc.find(entry => entry.key === nextKey[0])

            if (existingEntry) {
                existingEntry.nextKeys.push(nextKey[1])
            } else {
                acc.push({key: nextKey[0], nextKeys: [nextKey[1]]})
            }

            return acc
        }, [])
}

const pushKey = (key: string) => {
    if (key === 'Escape') {
        resetKeys()
        return
    }

    pushedKeys.value.push(key)

    getNextHotkeysSuggestions(lastThreePushedKeys.value)
    evalKeyCombo()
}

hotkeys('*', {keyup: true}, (keyevent) => {
    if (keyevent.type === 'keyup') {
        pushKey(keyevent.key)
    }
})

let allTimersPaused = false

let defaultTimer: ReturnType<typeof setTimeout>
let sequenceHitTimer: ReturnType<typeof setTimeout>

const startTimer = (type: 'sequenceHit' | 'default') => {
    if (type === 'default') defaultTimer = setTimeout(resetKeys, 3000)
    if (type === 'sequenceHit') sequenceHitTimer = setTimeout(resetKeys, 1000)

}

const resetTimer = (timer: ReturnType<typeof setTimeout>) => {
    if (!timer) return
    clearTimeout(timer)
}

const pauseTimers = () => {
    if (!allTimersPaused) {
        clearTimeout(defaultTimer)
        clearTimeout(sequenceHitTimer)
        allTimersPaused = true
    }
}

const resumeTimers = () => {
    if (allTimersPaused) {
        setTimeout(resetKeys, 1000)
        allTimersPaused = false
    }
}

const arrToLower = (arr: string[]) => {
    return arr.map(s => s.toLowerCase())
}

const arrEqIgnoreCase = (a: string[], b: string[]) => {
    return arrToLower(a).join('') === arrToLower(b).join('')
}

const evalKeyCombo = () => {
    resetTimer(defaultTimer)

    for (const [keys, handler] of sequences) {
        if (arrEqIgnoreCase(keys, lastThreePushedKeys.value)) {
            resetTimer(sequenceHitTimer)
            handler()
            startTimer('sequenceHit')
        }
    }

    if (pushedKeys.value.length > 0) {
        startTimer('default')
    }
}

export const hotkeySequence = (keys: string[], handler: Function) => {
    if (!keys.length) {
        return
    }
    sequences.set(arrToLower(keys), handler)
}

export const useHotkeys = () => ({lastThreePushedKeys, pauseTimers, resumeTimers, showHotkeysVisualizer, nextSuggestedHotkeysSequence})